import { useMemo } from "react";
import { formatCampaignTitleCase } from "../campaignFormatUtils";
import { formattedColorMaterial } from "../../../components/formatted/FormattedText";
import { normalizeMaterialKey } from "../../../shared/utils/materialNameNormalizer";
import { CHART_COLORS } from "../components/campaignChartConstants";
import { formatNonRemovalReason } from "../../../mobileapp/notifications/notificationNonRemovalReasons";

const DISTRIBUTION_BAR_COLORS = [
  "#00A7A6",
  "#f58142",
  "#42f55d",
  "#bf42f5",
  "#2ab5b8",
  "#FFA500",
  "#E95280",
];

const buildPieChartData = (entries, getLabel, getColor) => {
  if (entries.length === 0) {
    return { labels: [], values: [], colors: [], total: 0 };
  }

  const sorted = [...entries].sort((a, b) => b.value - a.value);

  return {
    labels: sorted.map((entry) => getLabel(entry)),
    values: sorted.map((entry) => Number(entry.value) || 0),
    colors: sorted.map((entry, index) =>
      getColor
        ? getColor(entry, index)
        : DISTRIBUTION_BAR_COLORS[index % DISTRIBUTION_BAR_COLORS.length],
    ),
    total: sorted.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0),
  };
};

export const useCampaignAnalysisData = (comments, t) => {
  const processedData = useMemo(() => {
    if (!comments || comments.length === 0) {
      return {
        alertsCount: 0,
        totalErrors: 0,
        totalErrorsRemoved: 0,
        retraitData: { yes: 0, no: 0 },
        errorsByCategory: {},
        errorsByCategoryLabel: {},
        nonRetraitReasons: {},
        emplacementData: {},
      };
    }

    let alertsCount = comments.length;
    let totalErrors = 0;
    let totalErrorsRemoved = 0;
    const retraitData = { yes: 0, no: 0 };
    const errorsByCategory = {};
    const errorsByCategoryLabel = {};
    const nonRetraitReasons = {};
    const emplacementData = {};

    comments.forEach((comment) => {
      const number = comment.number != null ? Number(comment.number) : 0;
      if (Number.isNaN(number)) {
        return;
      }
      totalErrors += number;

      if (comment.objectRemoved === true) {
        retraitData.yes += number;
        totalErrorsRemoved += number;

        const materialRaw = comment.material || t("Unknown");
        const materialKey = normalizeMaterialKey(materialRaw);
        const materialGroupKey = materialKey?.toLowerCase() || materialKey;
        if (!errorsByCategory[materialGroupKey]) {
          errorsByCategory[materialGroupKey] = 0;
          errorsByCategoryLabel[materialGroupKey] = materialKey;
        }
        errorsByCategory[materialGroupKey] += number;
      } else {
        retraitData.no += number;

        const reason = formatNonRemovalReason(comment, t);
        nonRetraitReasons[reason] = (nonRetraitReasons[reason] || 0) + 1;
      }

      const emplacement = comment.positioning
        ? formatCampaignTitleCase(comment.positioning)
        : t("Unknown");
      emplacementData[emplacement] = (emplacementData[emplacement] || 0) + 1;
    });

    return {
      alertsCount,
      totalErrors,
      totalErrorsRemoved,
      retraitData,
      errorsByCategory,
      errorsByCategoryLabel,
      nonRetraitReasons,
      emplacementData,
    };
  }, [comments, t]);

  const retraitChartData = useMemo(() => {
    const total = processedData.retraitData.yes + processedData.retraitData.no;
    if (total === 0) return { labels: [], values: [], colors: [], total: 0 };

    return {
      labels: [t("Yes"), t("No")],
      values: [
        Number(processedData.retraitData.yes) || 0,
        Number(processedData.retraitData.no) || 0,
      ],
      colors: [CHART_COLORS.PRIMARY, CHART_COLORS.GRAY],
      total: Number(total) || 0,
    };
  }, [processedData.retraitData, t]);

  const errorsByCategoryChartData = useMemo(() => {
    const entries = Object.keys(processedData.errorsByCategory).map((key) => ({
      key,
      value: processedData.errorsByCategory[key],
    }));

    return buildPieChartData(
      entries,
      (entry) => t(processedData.errorsByCategoryLabel[entry.key] || entry.key),
      (entry) =>
        formattedColorMaterial(
          processedData.errorsByCategoryLabel[entry.key] || entry.key,
        ),
    );
  }, [processedData.errorsByCategory, processedData.errorsByCategoryLabel, t]);

  const nonRetraitReasonsChartData = useMemo(() => {
    const entries = Object.keys(processedData.nonRetraitReasons).map(
      (reason) => ({
        key: reason,
        value: processedData.nonRetraitReasons[reason],
      }),
    );

    return buildPieChartData(
      entries,
      (entry) => entry.key,
      (_entry, index) =>
        DISTRIBUTION_BAR_COLORS[index % DISTRIBUTION_BAR_COLORS.length],
    );
  }, [processedData.nonRetraitReasons]);

  const emplacementChartData = useMemo(() => {
    const entries = Object.keys(processedData.emplacementData).map((key) => ({
      key,
      value: processedData.emplacementData[key],
    }));

    return buildPieChartData(
      entries,
      (entry) => entry.key,
      (_entry, index) =>
        DISTRIBUTION_BAR_COLORS[index % DISTRIBUTION_BAR_COLORS.length],
    );
  }, [processedData.emplacementData]);

  return {
    processedData,
    retraitChartData,
    errorsByCategoryChartData,
    nonRetraitReasonsChartData,
    emplacementChartData,
  };
};
